import { useEffect, useState } from "react";

/**
 * Fetches a backend URL with the current auth (Bearer token or session cookie),
 * turns the response body into a blob URL, and returns it for use in <img src>.
 *
 * Necessary because `<img>` requests don't attach the Authorization header the
 * SPA uses for API calls. Without this hook, an authenticated /api/.../file
 * endpoint would 401 when loaded as an image.
 *
 * The blob URL is revoked on unmount so we don't leak object URLs across the
 * lifetime of the session.
 */
export function useAuthenticatedImage(url: string | null | undefined): {
  src: string | null;
  loading: boolean;
  error: string | null;
} {
  const [src, setSrc] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!url) {
      setSrc(null);
      setError(null);
      return;
    }
    let cancelled = false;
    let objectUrl: string | null = null;
    setLoading(true);
    setError(null);

    (async () => {
      try {
        const { getAccessToken } = await import("@/lib/auth");
        const bearer = getAccessToken();
        const res = await fetch(url, {
          credentials: "include",
          headers: bearer ? { authorization: `Bearer ${bearer}` } : undefined,
        });
        if (!res.ok) throw new Error(`fetch failed: ${res.status}`);
        const blob = await res.blob();
        objectUrl = URL.createObjectURL(blob);
        if (!cancelled) setSrc(objectUrl);
      } catch (e) {
        if (!cancelled)
          setError(e instanceof Error ? e.message : "failed to load image");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [url]);

  return { src, loading, error };
}
